import clsx from 'clsx';
import { ArrowDropDown } from '@material-ui/icons';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { SelectContext } from './SelectContext';
import { Option } from '../Option';

interface SelectProps<T> {
  id?: string;
  children: ReactNode;
  className?: string;
  dropdownClassName?: string;
  color?: 'primary' | 'secondary' | 'sand';
  disabled?: boolean;
  emptyOptionText?: string;
  fullWidth?: boolean;
  menuPosition?: 'top' | 'bottom';
  name: string;
  onChange: (value: T) => void;
  value: T;
  variant?: 'default' | 'contained';
}

export function Select<T>(props: SelectProps<T>) {
  const {
    id,
    children,
    className,
    dropdownClassName,
    color = 'primary',
    disabled = false,
    emptyOptionText,
    fullWidth,
    menuPosition = 'bottom',
    name,
    onChange,
    value,
    variant = 'default',
  } = props;
  const [open, setOpen] = useState(false);
  const [selectedText, setSelectedText] = useState<ReactNode>();
  const classes = useStyles();
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (!selectRef.current?.contains(event.target as HTMLElement))
        setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);

    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleChange = (value: T) => {
    setOpen(false);
    onChange(value);
  };

  return (
    <div
      className={clsx(classes.container, { [classes.fullWidth]: fullWidth })}
      ref={selectRef}
    >
      <div
        className={clsx(classes.selectContainer, {
          'flex-col-reverse': menuPosition === 'top',
        })}
      >
        <button
          aria-expanded={open}
          aria-haspopup="listbox"
          id={id ?? `select-root-${name}`}
          className={clsx(
            className,
            classes.select,
            classes.selectVariant[variant].base,
            classes.selectVariant[variant][color]
          )}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          type="button"
        >
          <span className={classes.selectText}>
            {selectedText ?? emptyOptionText}
          </span>
          <span
            className={clsx(classes.arrow, { [classes.arrowRotated]: open })}
          >
            <ArrowDropDown />
          </span>
        </button>

        <ul
          className={clsx(
            dropdownClassName,
            classes.dropdown,
            classes.dropdownVariant[variant],
            {
              [classes.fullWidth]: fullWidth,
              'mb-8 !mt-0': menuPosition === 'top',
              hidden: !open,
            }
          )}
        >
          <SelectContext
            color={color}
            onChange={handleChange}
            selectedText={selectedText}
            setSelectedText={setSelectedText}
            value={value}
          >
            {emptyOptionText ? (
              <Option value={undefined}>{emptyOptionText}</Option>
            ) : null}
            {children}
          </SelectContext>
        </ul>
      </div>
    </div>
  );
}

const useStyles = () => ({
  arrow:
    'pointer-events-none absolute inset-y-0 right-0 flex items-center px-1',
  arrowRotated: '-rotate-180',
  container: 'inline-block',
  dropdown:
    'absolute z-[9999] w-max mt-8 max-h-60 overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
  dropdownVariant: {
    default: 'bg-background-light dark:bg-background-dark',
    contained: 'bg-gray-300 dark:bg-[#1B1E20]',
  },
  fullWidth: '!w-full',
  select:
    'relative flex items-center cursor-pointer h-8 py-1 pl-1 pr-6 text-left sm:text-sm shadow-sm focus:outline-none disabled:opacity-30 disabled:pointer-events-none',
  selectContainer: 'relative flex flex-col',
  selectText:
    'whitespace-nowrap pointer-events-none text-ellipsis overflow-hidden',
  selectVariant: {
    default: {
      base: 'border-b',
      primary: 'focus:border-b-primary',
      secondary: 'focus:border-b-secondary',
      sand: 'focus:border-b-sand',
    },
    contained: {
      base: 'rounded bg-gray-100 dark:bg-[#1B1E20] border border-transparent focus:ring-1',
      primary: 'focus:border-primary focus:ring-primary',
      secondary: 'focus:border-secondary focus:ring-secondary',
      sand: 'focus:border-sand focus:ring-sand',
    },
  },
});
